import React, { useRef, useState } from 'react';
import { renderMathHtml } from '../../utils/renderMath';

const formatType = (type: string): string => {
  if (!type) return '';
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l: string) => l.toUpperCase());
};

const MIN_WIDTH = 350;
const MAX_WIDTH = 0.95 * window.innerWidth;

const AnswerDetails: React.FC<{ question: any }> = ({ question: q }) => {
  // Resizable width logic
  const [width, setWidth] = useState(700);
  const resizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(700);

  const handleMouseDown = (e: React.MouseEvent) => {
    resizing.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'ew-resize';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!resizing.current) return;
    let newWidth = startWidth.current + (startX.current - e.clientX);
    newWidth = Math.max(MIN_WIDTH, Math.min(newWidth, MAX_WIDTH));
    setWidth(newWidth);
  };

  const handleMouseUp = () => {
    resizing.current = false;
    document.body.style.cursor = '';
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  // Render question header (type, content, status, marks)
  const renderHeader = () => {
    return (
      <div className="mb-4 p-4 bg-gray-50 rounded border border-gray-200">
        <div className=" text-xs text-gray-600">
          <b>Type:</b> {formatType(q.question_type)}
          {q.status && (
            <span className="ml-4"><b>Status:</b> {formatType(q.status)}</span>
          )}
          {typeof q.marks_obtained !== 'undefined' && (
            <span className="ml-4"><b>Marks:</b> {q.marks_obtained}</span>
          )}
        </div>
        <div className="text-base font-semibold text-gray-900">
          {q.question_content ? (
            <div className="exam-content leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMathHtml(q.question_content) }} />
          ) : (
            <span className="text-gray-400">No question content</span>
          )}
        </div>
      </div>
    );
  };

  // Render question content at the top
  const renderQuestionContent = () => {
    if (q.question_content) {
      return (
        <div className="mb-4 text-gray-900 font-medium">
          <div className="exam-content leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMathHtml(q.question_content) }} />
        </div>
      );
    }
    return null;
  };

  // Matrix Match UI
  if (q.question_type === 'matrix_match' && q.student_answer_raw && q.correct_answer_raw) {
    let rows = q.matrix_rows || q.rows || [];
    let columns = q.matrix_columns || q.columns || [];
    if ((!rows.length || !columns.length) && q.correct_answer) {
      if (Array.isArray(q.correct_answer)) {
        const rowSet = new Set();
        const colSet = new Set();
        q.correct_answer.forEach((m: any) => {
          if (m.row) rowSet.add(m.row);
          if (m.column) colSet.add(m.column);
        });
        rows = Array.from(rowSet);
        columns = Array.from(colSet);
      }
    }
    if (!rows.length) rows = ['Row 1', 'Row 2', 'Row 3'];
    if (!columns.length) columns = ['Col 1', 'Col 2', 'Col 3'];
    const studentMatches = (q.student_answer_raw.matches || []);
    const correctMatches = (q.correct_answer_raw.matches || []);
    const studentMap = new Map(studentMatches.map((m: any) => [String((m.row_id || m.row) + '-' + (m.column_id || m.column)), true]));
    const correctMap = new Map(correctMatches.map((m: any) => [String((m.row_id || m.row) + '-' + (m.column_id || m.column)), true]));
    return (
      <div>
        <div className="font-semibold mb-2">Matrix Match Table:</div>
        <div className="overflow-x-auto">
          <table className="min-w-full border bg-gray-50">
            <thead>
              <tr>
                <th className="border px-2 py-1"></th>
                {columns.map((col: any, i: number) => (
                  <th key={i} className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(typeof col === 'object' ? (col.text || col) : col) }} /></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any, rowIdx: number) => (
                <tr key={rowIdx}>
                  <td className="border px-2 py-1 font-medium"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(typeof row === 'object' ? (row.text || row) : row) }} /></td>
                  {columns.map((col: any, colIdx: number) => {
                    const key = String((row.id || rowIdx + 1) + '-' + (col.id || colIdx + 1));
                    const isStudent = studentMap.has(key);
                    const isCorrect = correctMap.has(key);
                    return (
                      <td key={colIdx} className="border px-2 py-1 text-center">
                        {isStudent && <span style={{ color: 'green', fontWeight: 'bold', marginRight: isCorrect ? 4 : 0 }}>✔️</span>}
                        {isCorrect && <span style={{ color: 'blue', fontWeight: 'bold' }}>●</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex gap-4 text-sm">
          <span><span style={{ color: 'green', fontWeight: 'bold' }}>✔️</span> Your Answer</span>
          <span><span style={{ color: 'blue', fontWeight: 'bold' }}>●</span> Correct Answer</span>
        </div>
      </div>
    );
  }
  // Three Column Match UI
  if (q.question_type === 'three_column_match' && Array.isArray(q.student_answer) && Array.isArray(q.correct_answer)) {
    const studentMatches = q.student_answer;
    const correctMatches = q.correct_answer;
    const rowCount = Math.max(studentMatches.length, correctMatches.length);
    const qd = q.question_data || {};
    const columnA = Array.isArray(qd.column_a_items) ? qd.column_a_items : [];
    const columnB = Array.isArray(qd.column_b_items) ? qd.column_b_items : [];
    const columnC = Array.isArray(qd.column_c_items) ? qd.column_c_items : [];
    const getVal = (val: any, arr: any[]) => {
      if (typeof val === 'string') return val;
      if (typeof val === 'number' && arr.length) return arr[val] || val;
      return val ?? '';
    };
    return (
      <div>
        <div className="font-semibold mb-2">Three Column Match Table:</div>
        <div className="overflow-x-auto">
          <table className="min-w-full border bg-gray-50">
            <thead>
              <tr>
                <th className="border px-2 py-1">#</th>
                <th className="border px-2 py-1">Student A</th>
                <th className="border px-2 py-1">Student B</th>
                <th className="border px-2 py-1">Student C</th>
                <th className="border px-2 py-1">Correct A</th>
                <th className="border px-2 py-1">Correct B</th>
                <th className="border px-2 py-1">Correct C</th>
                <th className="border px-2 py-1">Result</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rowCount }).map((_, idx) => {
                const student = studentMatches[idx] || {};
                const correct = correctMatches[idx] || {};
                const studentA = getVal(student.a, columnA);
                const studentB = getVal(student.b, columnB);
                const studentC = getVal(student.c, columnC);
                const correctA = getVal(correct.a, columnA);
                const correctB = getVal(correct.b, columnB);
                const correctC = getVal(correct.c, columnC);
                const isRowCorrect = studentA === correctA && studentB === correctB && studentC === correctC;
                return (
                  <tr key={idx} style={{ background: isRowCorrect ? '#e6ffe6' : '#ffe6e6' }}>
                    <td className="border px-2 py-1">{idx + 1}</td>
                    <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(studentA ?? '')) }} /></td>
                    <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(studentB ?? '')) }} /></td>
                    <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(studentC ?? '')) }} /></td>
                    <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(correctA ?? '')) }} /></td>
                    <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(correctB ?? '')) }} /></td>
                    <td className="border px-2 py-1"><div dangerouslySetInnerHTML={{ __html: renderMathHtml(String(correctC ?? '')) }} /></td>
                    <td className="border px-2 py-1 text-lg">
                      {isRowCorrect ? (
                        <span style={{ color: 'green' }}>✔</span>
                      ) : (
                        <span style={{ color: 'red' }}>✘</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex gap-4 text-sm">
          <span><span style={{ color: 'green', fontWeight: 'bold' }}>✔</span> Correct Row</span>
          <span><span style={{ color: 'red', fontWeight: 'bold' }}>✘</span> Incorrect Row</span>
        </div>
      </div>
    );
  }
  // Comprehension UI
  if (q.question_type === 'comprehension' && Array.isArray(q.sub_questions)) {
    const qd = q.question_data || {};
    const paragraphs = qd.comprehension_paragraphs || [];
    return (
      <div>
        {paragraphs.length > 0 && (
          <div className="mb-4">
            {paragraphs.map((p: any, i: number) => (
              <div key={i} className="bg-gray-100 rounded px-4 py-2 mb-2">Paragraph {i + 1}<br /><span dangerouslySetInnerHTML={{ __html: renderMathHtml(p.paragraph_text) }} /></div>
            ))}
          </div>
        )}
        <div className="font-semibold mb-2">Sub-Questions:</div>
        {q.sub_questions.map((subq: any, i: number) => (
          <div key={subq.sub_question_id} className="mb-4 p-3 bg-white border rounded">
            <div className="font-medium ">Q{subq.sub_question_id}: <span dangerouslySetInnerHTML={{ __html: renderMathHtml(subq.content) }} /></div>
            <div className="text-xs text-gray-600 ">
              Type: {subq.type ? subq.type.replace(/_/g, ' ') : 'N/A'}
            </div>
            <div>
              <span style={{ color: '#2563eb' }}>Your Answer:</span>{' '}
              {(() => {
                if (subq.type === 'single_correct_1' && typeof subq.student_answer === 'string') {
                  const ans = subq.answers?.find((a: any) => a.option_label === subq.student_answer);
                  return (
                    <span>
                      {subq.student_answer}: {ans ? <span dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} /> : ''}
                      {subq.is_correct ? <span style={{ color: 'green', marginLeft: 8 }}>✔</span> : <span style={{ color: 'red', marginLeft: 8 }}>✘</span>}
                    </span>
                  );
                }
                if (subq.type === 'multiple_correct' && Array.isArray(subq.student_answer)) {
                  return (
                    <span>
                      {subq.student_answer.map((label: string) => {
                        const ans = subq.answers?.find((a: any) => a.option_label === label);
                        return (
                          <span key={label} style={{ marginRight: 8 }}>
                            {label}: {ans ? <span dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} /> : ''}
                          </span>
                        );
                      })}
                      {subq.is_correct ? <span style={{ color: 'green', marginLeft: 8 }}>✔</span> : <span style={{ color: 'red', marginLeft: 8 }}>✘</span>}
                    </span>
                  );
                }
                if ((subq.type === 'integer_type' || subq.type === 'numerical_type') && subq.student_answer !== null && subq.student_answer !== undefined) {
                  return (
                    <span>
                      {subq.student_answer}
                      {subq.is_correct ? <span style={{ color: 'green', marginLeft: 8 }}>✔</span> : <span style={{ color: 'red', marginLeft: 8 }}>✘</span>}
                    </span>
                  );
                }
                return <span className="text-gray-400">N/A</span>;
              })()}
            </div>
            <div>
              <span style={{ color: '#059669' }}>Correct Answer:</span>{' '}
              {(() => {
                if (subq.type === 'single_correct_1' && subq.correct_answer?.label) {
                  const ans = subq.answers?.find((a: any) => a.option_label === subq.correct_answer.label);
                  return <span>{subq.correct_answer.label}: {ans ? <span dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} /> : ''}</span>;
                }
                if (subq.type === 'multiple_correct' && Array.isArray(subq.correct_answer?.labels)) {
                  return (
                    <span>
                      {subq.correct_answer.labels.map((label: string) => {
                        const ans = subq.answers?.find((a: any) => a.option_label === label);
                        return (
                          <span key={label} style={{ marginRight: 8 }}>
                            {label}: {ans ? <span dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} /> : ''}
                          </span>
                        );
                      })}
                    </span>
                  );
                }
                if ((subq.type === 'integer_type' || subq.type === 'numerical_type') && subq.correct_answer?.value !== null && subq.correct_answer?.value !== undefined) {
                  return <span>{subq.correct_answer.value}</span>;
                }
                return <span className="text-gray-400">N/A</span>;
              })()}
            </div>
          </div>
        ))}
      </div>
    );
  }
  // Default fallback (old logic)
  return (
    <div style={{ width: width + 'px', position: 'relative' }}>
      {/* Resize handle */}
      <div
        style={{ position: 'absolute', left: 0, top: 0, width: 8, height: '100%', cursor: 'ew-resize', zIndex: 10 }}
        onMouseDown={handleMouseDown}
      />
      {renderHeader()}
      <div className="mb-4">
        <h3 className="font-semibold mb-2">Your Answer</h3>
        {(() => {
          // String answer (may contain HTML and/or LaTeX)
          if (typeof q.student_answer === 'string') {
            return <div className="exam-content" dangerouslySetInnerHTML={{ __html: renderMathHtml(q.student_answer) }} />;
          }
          // Array of answers
          if (Array.isArray(q.student_answer)) {
            return q.student_answer.map((ans: any, i: number) =>
              typeof ans === 'string'
                ? <div key={i} className="exam-content" dangerouslySetInnerHTML={{ __html: renderMathHtml(ans) }} />
                : <div key={i}>{String(ans)}</div>
            );
          }
          // Object answers (including special cases)
          if (q.student_answer && typeof q.student_answer === 'object') {
            if (
              'min_value' in q.student_answer &&
              'max_value' in q.student_answer &&
              'exact_answer' in q.student_answer
            ) {
              return (
                <div>
                  <b>Range:</b> {q.student_answer.min_value} to {q.student_answer.max_value}<br />
                  <b>Exact:</b> {q.student_answer.exact_answer}
                </div>
              );
            }
            // Render any string fields with HTML if present
            return (
              <div>
                {Object.entries(q.student_answer).map(([k, v]) => (
                  <div key={k}>
                    <b>{k}:</b>{' '}
                    {typeof v === 'string'
                      ? <div className="exam-content inline-block" dangerouslySetInnerHTML={{ __html: renderMathHtml(v) }} />
                      : String(v)}
                  </div>
                ))}
              </div>
            );
          }
          // Null or plain text
          if (q.student_answer === null || typeof q.student_answer === 'undefined') {
            return <span className="text-gray-400">N/A</span>;
          }
          return <span dangerouslySetInnerHTML={{ __html: renderMathHtml(String(q.student_answer)) }} />;
        })()}
      </div>
      <div>
        <h3 className="font-semibold mb-2">Correct Answer</h3>
        {(() => {
          if (typeof q.correct_answer === 'string') {
            return <div className="exam-content" dangerouslySetInnerHTML={{ __html: renderMathHtml(q.correct_answer) }} />;
          }
          if (Array.isArray(q.correct_answer)) {
            return q.correct_answer.map((ans: any, i: number) =>
              typeof ans === 'string'
                ? <div key={i} className="exam-content" dangerouslySetInnerHTML={{ __html: renderMathHtml(ans) }} />
                : <div key={i}>{String(ans)}</div>
            );
          }
          if (q.correct_answer && typeof q.correct_answer === 'object') {
            if (
              'min_value' in q.correct_answer &&
              'max_value' in q.correct_answer &&
              'exact_answer' in q.correct_answer
            ) {
              return (
                <div>
                  <b>Range:</b> {q.correct_answer.min_value} to {q.correct_answer.max_value}<br />
                  <b>Exact:</b> {q.correct_answer.exact_answer}
                </div>
              );
            }
            return (
              <div>
                {Object.entries(q.correct_answer).map(([k, v]) => (
                  <div key={k}>
                    <b>{k}:</b>{' '}
                    {typeof v === 'string'
                      ? <div className="exam-content inline-block" dangerouslySetInnerHTML={{ __html: renderMathHtml(v) }} />
                      : String(v)}
                  </div>
                ))}
              </div>
            );
          }
          if (q.correct_answer === null || typeof q.correct_answer === 'undefined') {
            return <span className="text-gray-400">N/A</span>;
          }
          return <span dangerouslySetInnerHTML={{ __html: renderMathHtml(String(q.correct_answer)) }} />;
        })()}
      </div>
    </div>
  );
};

export default AnswerDetails; 